import { MNJLOGOBASE64 } from "./constants";
import endCoverHeaderLogo from "../assets/endcoverheaderlogo.png";
import endCoverImg1 from "../assets/endcoverimg1.png";
import endCoverImg2 from "../assets/endcoverimg2.png";

export interface LocationData {
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  phoneNumber: string;
}

export interface CoverSheetData {
  projectName: string;
  clientName: string;
  region: string;
  jobType: "Groundworks" | "Roads & Sewers" | "Combined";
  tenderTotal?: number;
  dateCreated?: Date;
  description?: string;
  siteAddress?: string;
  locationData?: LocationData;
}

export interface ExcelWorkbook {
  addImage: (
    options:
      | { base64: string; extension: string }
      | { buffer: ArrayBuffer; extension: string; name?: string },
  ) => number;
  addWorksheet: (name: string, position?: number) => any;
}

/**
 * Creates a professional cover sheet for Excel exports matching the tender document format
 * This will be inserted as the first worksheet in the workbook
 */
export const createCoverSheet = (
  workbook: ExcelWorkbook,
  data: CoverSheetData,
): void => {
  console.log("🎨 Creating cover sheet...");

  try {
    // Add the cover sheet as the first worksheet (index 0)
    const coverSheet = workbook.addWorksheet("Cover", 0);

    // Set column widths for proper layout
    coverSheet.columns = [
      { width: 15 }, // A
      { width: 15 }, // B
      { width: 15 }, // C
      { width: 15 }, // D
      { width: 15 }, // E
      { width: 15 }, // F
    ];

    // Configure view settings - hide gridlines and set print layout
    coverSheet.views = [
      {
        showGridLines: false,
        zoomScale: 100,
        zoomScaleNormal: 100,
      },
    ];

    // Set print settings
    coverSheet.pageSetup = {
      paperSize: 9, // A4
      orientation: "portrait",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
    };

    // Colors
    const borderColor = "0000FF"; // Blue border
    const yellowBg = "FFFF00"; // Yellow background for date
    const blackText = "000000";
    const grayText = "999999";

    // Helper function to apply blue border to a range
    const applyBlueBorder = (startRow: number, endRow: number) => {
      // Top border
      for (let col = 1; col <= 6; col++) {
        const cell = coverSheet.getCell(startRow, col);
        cell.border = {
          ...cell.border,
          top: { style: "thick", color: { argb: borderColor } },
        };
      }
      // Bottom border
      for (let col = 1; col <= 6; col++) {
        const cell = coverSheet.getCell(endRow, col);
        cell.border = {
          ...cell.border,
          bottom: { style: "thick", color: { argb: borderColor } },
        };
      }
      // Left border
      for (let row = startRow; row <= endRow; row++) {
        const cell = coverSheet.getCell(row, 1);
        cell.border = {
          ...cell.border,
          left: { style: "thick", color: { argb: borderColor } },
        };
      }
      // Right border
      for (let row = startRow; row <= endRow; row++) {
        const cell = coverSheet.getCell(row, 6);
        cell.border = {
          ...cell.border,
          right: { style: "thick", color: { argb: borderColor } },
        };
      }
    };

    let currentRow = 2;

    // Row 2-3: Company Name
    const companyCell = coverSheet.getCell(`A${currentRow}`);
    companyCell.value = "M & J EVANS CONSTRUCTION LIMITED";
    coverSheet.mergeCells(`A${currentRow}:F${currentRow + 1}`);
    companyCell.font = {
      bold: true,
      size: 18,
      color: { argb: blackText },
      name: "Calibri",
    };
    companyCell.alignment = { horizontal: "center", vertical: "middle" };
    coverSheet.getRow(currentRow).height = 25;
    coverSheet.getRow(currentRow + 1).height = 25;
    currentRow += 2;

    // Row 4: FOR
    const forCell = coverSheet.getCell(`A${currentRow}`);
    forCell.value = "FOR";
    coverSheet.mergeCells(`A${currentRow}:F${currentRow}`);
    forCell.font = {
      bold: true,
      size: 14,
      color: { argb: blackText },
      name: "Calibri",
    };
    forCell.alignment = { horizontal: "center", vertical: "middle" };
    coverSheet.getRow(currentRow).height = 25;
    currentRow++;

    // Row 5: Spacer
    coverSheet.getRow(currentRow).height = 15;
    currentRow++;

    // Row 6: Client Name
    const clientCell = coverSheet.getCell(`A${currentRow}`);
    clientCell.value = data.clientName || "ENTER CLIENT NAME";
    coverSheet.mergeCells(`A${currentRow}:F${currentRow}`);
    clientCell.font = {
      bold: true,
      size: 14,
      color: { argb: blackText },
      name: "Calibri",
    };
    clientCell.alignment = { horizontal: "center", vertical: "middle" };
    coverSheet.getRow(currentRow).height = 25;
    currentRow++;

    // Row 7: Horizontal line
    const line1Cell = coverSheet.getCell(`A${currentRow}`);
    coverSheet.mergeCells(`A${currentRow}:F${currentRow}`);
    line1Cell.border = {
      bottom: { style: "thick", color: { argb: blackText } },
    };
    coverSheet.getRow(currentRow).height = 5;
    currentRow++;

    // Row 8-9: TENDER heading
    const tenderCell = coverSheet.getCell(`A${currentRow}`);
    tenderCell.value = "TENDER";
    coverSheet.mergeCells(`A${currentRow}:F${currentRow + 1}`);
    tenderCell.font = {
      bold: true,
      size: 28,
      color: { argb: blackText },
      name: "Calibri",
    };
    tenderCell.alignment = { horizontal: "center", vertical: "middle" };
    coverSheet.getRow(currentRow).height = 30;
    coverSheet.getRow(currentRow + 1).height = 30;
    currentRow += 2;

    // Row 10: Horizontal line
    const line2Cell = coverSheet.getCell(`A${currentRow}`);
    coverSheet.mergeCells(`A${currentRow}:F${currentRow}`);
    line2Cell.border = {
      bottom: { style: "thick", color: { argb: blackText } },
    };
    coverSheet.getRow(currentRow).height = 5;
    currentRow++;

    // Row 11: Spacer
    coverSheet.getRow(currentRow).height = 20;
    currentRow++;

    // Row 12-15: Site Address
    const siteAddressCell = coverSheet.getCell(`A${currentRow}`);
    siteAddressCell.value = data.siteAddress || "ENTER SITE ADDRESS";
    coverSheet.mergeCells(`A${currentRow}:F${currentRow + 3}`);
    siteAddressCell.font = {
      bold: true,
      size: 12,
      color: { argb: blackText },
      name: "Calibri",
    };
    siteAddressCell.alignment = { horizontal: "center", vertical: "middle" };
    coverSheet.getRow(currentRow).height = 25;
    coverSheet.getRow(currentRow + 1).height = 25;
    coverSheet.getRow(currentRow + 2).height = 25;
    coverSheet.getRow(currentRow + 3).height = 25;
    currentRow += 4;

    // Row 16: Work description
    const workDescCell = coverSheet.getCell(`A${currentRow}`);
    const workDesc =
      data.jobType === "Groundworks"
        ? "FOUNDATIONS; FLOORS; PLOT DRAINAGE & EXTERNAL WORKS"
        : data.jobType === "Roads & Sewers"
          ? "ROADS & SEWERS"
          : "COMBINED WORKS";
    workDescCell.value = workDesc;
    coverSheet.mergeCells(`A${currentRow}:F${currentRow}`);
    workDescCell.font = {
      bold: true,
      size: 11,
      color: { argb: blackText },
      name: "Calibri",
    };
    workDescCell.alignment = { horizontal: "center", vertical: "middle" };
    coverSheet.getRow(currentRow).height = 25;
    currentRow++;

    // Row 17-18: Date section with yellow background
    const dateCell = coverSheet.getCell(`A${currentRow}`);
    const dateValue = data.dateCreated
      ? data.dateCreated.toLocaleDateString("en-GB", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "ENTER DATE HERE";
    dateCell.value = dateValue;
    coverSheet.mergeCells(`A${currentRow}:F${currentRow + 1}`);
    dateCell.font = {
      bold: true,
      size: 12,
      color: { argb: blackText },
      name: "Calibri",
    };
    dateCell.alignment = { horizontal: "center", vertical: "middle" };
    dateCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: yellowBg },
    };
    coverSheet.getRow(currentRow).height = 25;
    coverSheet.getRow(currentRow + 1).height = 25;
    currentRow += 2;

    // Add spacers to push footer to bottom
    for (let i = 0; i < 10; i++) {
      coverSheet.getRow(currentRow).height = 20;
      currentRow++;
    }

    // Footer: Site address (using location data if available)
    const footerCell = coverSheet.getCell(`A${currentRow}`);

    // Format location data if available, otherwise use site address
    if (data.locationData) {
      const locationLines = [
        data.locationData.addressLine1,
        data.locationData.addressLine2,
        data.locationData.city,
        data.locationData.postalCode,
        data.locationData.phoneNumber,
      ].filter((line) => line && line.trim() !== ""); // Remove empty lines

      footerCell.value = locationLines.join("\n");
    } else {
      footerCell.value = data.siteAddress || "ENTER SITE ADDRESS";
    }

    coverSheet.mergeCells(`A${currentRow}:F${currentRow + 4}`);
    footerCell.font = {
      bold: false,
      size: 9,
      color: { argb: blackText },
      name: "Calibri",
    };
    footerCell.alignment = {
      horizontal: "left",
      vertical: "bottom",
      wrapText: true,
    };
    for (let i = 0; i < 5; i++) {
      coverSheet.getRow(currentRow + i).height = 15;
    }
    currentRow += 5;

    // Apply blue border around the entire page (rows 2 to currentRow)
    applyBlueBorder(2, currentRow);

    console.log("✅ Cover sheet created successfully");
  } catch (error) {
    console.error("❌ Failed to create cover sheet:", error);
    throw error;
  }
};

/**
 * Helper function to convert image path to ArrayBuffer for ExcelJS (browser-compatible)
 */
const getImageBuffer = async (imagePath: string): Promise<ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;

        ctx.drawImage(img, 0, 0);

        canvas.toBlob(
          async (blob) => {
            if (blob) {
              const arrayBuffer = await blob.arrayBuffer();
              resolve(arrayBuffer);
            } else {
              reject(new Error("Failed to convert canvas to blob"));
            }
          },
          "image/png",
          0.95,
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = (error) => {
      console.error("Image load error:", error);
      reject(new Error("Failed to load image"));
    };

    img.src = imagePath;
  });
};

/**
 * Creates an end cover sheet for Excel exports matching the tender document format
 * This will be inserted as the last worksheet in the workbook
 */
export const createEndCover = async (
  workbook: ExcelWorkbook,
  data: CoverSheetData,
): Promise<void> => {
  console.log("🎨 Creating end cover sheet...");
  console.log("📋 Cover data:", data);

  try {
    // Add the end cover sheet as the last worksheet
    console.log("📄 Adding worksheet...");
    const endCoverSheet = workbook.addWorksheet("End Cover");
    console.log("✅ Worksheet added successfully");

    // Set column widths for proper layout - using full page width
    endCoverSheet.columns = [
      { width: 1 }, // A - left margin (reduced)
      { width: 2 }, // B - inside padding
      { width: 15 }, // C - content
      { width: 15 }, // D - content
      { width: 15 }, // E - content
      { width: 15 }, // F - content
      { width: 15 }, // G - content
      { width: 2 }, // H - inside padding
      { width: 1 }, // I - right margin (reduced)
    ];

    // Configure view settings - hide gridlines and set print layout
    endCoverSheet.views = [
      {
        showGridLines: false,
        zoomScale: 100,
        zoomScaleNormal: 100,
      },
    ];

    // Set print settings - fit to single page with minimal margins for full width
    endCoverSheet.pageSetup = {
      paperSize: 9, // A4
      orientation: "portrait",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 1, // Fit to single page height
      scale: 100,
      margins: {
        left: 0.3,
        right: 0.3,
        top: 0.4,
        bottom: 0.4,
        header: 0.2,
        footer: 0.2,
      },
    };

    // Colors - matching the reference image
    const yellowBg = "FFFF00";
    const blueBorder = "4472C4"; // Blue border color
    const blueText = "4472C4"; // Blue text for footer
    const footerBlueBg = "4F81BD"; // Footer blue background color
    const blackText = "000000";

    // Add top padding (empty rows before border) - reduced
    let currentRow = 1;
    endCoverSheet.getRow(currentRow).height = 10;
    currentRow++;

    // Start border row
    const borderStartRow = currentRow;

    // Add padding inside border (empty row)
    endCoverSheet.getRow(currentRow).height = 15;
    currentRow++;

    // ===== YELLOW HEADER SECTION =====
    const headerCell = endCoverSheet.getCell(`C${currentRow}`);
    const headerText = `M & J EVANS CONSTRUCTION LIMITED ${data.region || "REGION"} - ${
      data.jobType || "JOB TYPE"
    } Tender Submission.\n[${data.clientName || "CLIENT NAME"}] - [${
      data.siteAddress || "SITE ADDRESS"
    }] - [${
      data.dateCreated
        ? data.dateCreated.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })
        : "DATE HERE"
    }]`;
    headerCell.value = headerText;
    endCoverSheet.mergeCells(`C${currentRow}:F${currentRow + 1}`);
    headerCell.font = {
      bold: true,
      size: 10,
      color: { argb: blackText },
      name: "Calibri",
    };
    headerCell.alignment = {
      horizontal: "left",
      vertical: "middle",
      wrapText: true,
    };
    headerCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: yellowBg },
    };

    // Apply borders to all cells in the header
    for (let col = 3; col <= 7; col++) {
      // Changed from col 2-7 to 3-7 (C-G)
      for (let row = currentRow; row <= currentRow + 1; row++) {
        const cell = endCoverSheet.getCell(row, col);
        cell.border = {
          top: { style: "thin", color: { argb: blueBorder } },
          bottom: { style: "thin", color: { argb: blueBorder } },
          left: { style: "thin", color: { argb: blueBorder } },
          right: { style: "thin", color: { argb: blueBorder } },
        };
        // Apply yellow background only to text area (C-F), not logo area (G)
        if (col <= 6 && !cell.fill) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: yellowBg },
          };
        }
      }
    }

    // Load and add header logo in top right
    try {
      console.log("🖼️ Loading header logo...");
      const headerLogoBuffer = await getImageBuffer(endCoverHeaderLogo);
      console.log("✅ Header logo buffer loaded");
      const headerLogoId = workbook.addImage({
        buffer: headerLogoBuffer,
        extension: "png",
      });
      console.log("✅ Header logo added to workbook, ID:", headerLogoId);
      endCoverSheet.addImage(headerLogoId, {
        tl: { col: 6.5, row: currentRow - 0.5 } as any, // Adjusted column position
        ext: { width: 60, height: 60 },
      });
      console.log("✅ Header logo placed on sheet");
    } catch (error) {
      console.warn("⚠️ Could not load header logo:", error);
    }

    endCoverSheet.getRow(currentRow).height = 30;
    endCoverSheet.getRow(currentRow + 1).height = 30;
    currentRow += 2;

    // Small spacer
    endCoverSheet.getRow(currentRow).height = 8;
    currentRow++;

    // ===== "OUR VALUES" SECTION =====
    const valuesHeaderCell = endCoverSheet.getCell(`C${currentRow}`);
    valuesHeaderCell.value = "Our values.";
    endCoverSheet.mergeCells(`C${currentRow}:G${currentRow}`);
    valuesHeaderCell.font = {
      bold: true,
      size: 11,
      color: { argb: blackText },
      name: "Calibri",
    };
    valuesHeaderCell.alignment = { horizontal: "left", vertical: "middle" };

    // Add border to header row
    for (let col = 3; col <= 7; col++) {
      const cell = endCoverSheet.getCell(currentRow, col);
      cell.border = {
        top: { style: "medium", color: { argb: blueBorder } },
        left: { style: "medium", color: { argb: blueBorder } },
        right: { style: "medium", color: { argb: blueBorder } },
      };
    }
    endCoverSheet.getRow(currentRow).height = 22;
    currentRow++;

    // Values content
    const valuesTextCell = endCoverSheet.getCell(`C${currentRow}`);
    valuesTextCell.value =
      "Quality. Collaboration. Accountability. Integrity. Excellence";
    endCoverSheet.mergeCells(`C${currentRow}:G${currentRow}`);
    valuesTextCell.font = {
      size: 10,
      color: { argb: blackText },
      name: "Calibri",
    };
    valuesTextCell.alignment = { horizontal: "center", vertical: "middle" };

    // Add border to content row
    for (let col = 3; col <= 7; col++) {
      const cell = endCoverSheet.getCell(currentRow, col);
      cell.border = {
        bottom: { style: "medium", color: { argb: blueBorder } },
        left: { style: "medium", color: { argb: blueBorder } },
        right: { style: "medium", color: { argb: blueBorder } },
      };
    }
    endCoverSheet.getRow(currentRow).height = 22;
    currentRow++;

    // Spacer
    endCoverSheet.getRow(currentRow).height = 8;
    currentRow++;

    // ===== "SUSTAINABILITY" SECTION =====
    const sustainabilityHeaderCell = endCoverSheet.getCell(`C${currentRow}`);
    sustainabilityHeaderCell.value =
      "Sustainability – a carbon neutral business.";
    endCoverSheet.mergeCells(`C${currentRow}:G${currentRow}`);
    sustainabilityHeaderCell.font = {
      bold: true,
      size: 11,
      color: { argb: blackText },
      name: "Calibri",
    };
    sustainabilityHeaderCell.alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    // Add border to header row
    for (let col = 3; col <= 7; col++) {
      const cell = endCoverSheet.getCell(currentRow, col);
      cell.border = {
        top: { style: "medium", color: { argb: blueBorder } },
        left: { style: "medium", color: { argb: blueBorder } },
        right: { style: "medium", color: { argb: blueBorder } },
      };
    }
    endCoverSheet.getRow(currentRow).height = 22;
    currentRow++;

    // Sustainability content - updated text
    const sustainabilityText = `At M&J we are acutely aware of the impact of our activities on the environment. The nature of our work means we are using machinery that burns fossil fuels. We are working with major manufacturers to select low emission equipment and trial the latest technology, such as Hybrid excavators. We have moved much of our company car fleet to Hybrid and electric; and our head office is now solar powered. We then purchase carbon credits to offset the emissions we cannot currently avoid.

M&J have signed up the Science Based Targets Initiative and are gold members of the Supply Chain Sustainability school.

We can work with you to reduce the carbon impact of build, sourcing materials such as low carbon concrete. Unless requested, these are not included in this tender, but we can provide costs as required.`;

    const sustainabilityTextCell = endCoverSheet.getCell(`C${currentRow}`);
    sustainabilityTextCell.value = sustainabilityText;
    endCoverSheet.mergeCells(`C${currentRow}:G${currentRow + 8}`);
    sustainabilityTextCell.font = {
      size: 9,
      color: { argb: blackText },
      name: "Calibri",
    };
    sustainabilityTextCell.alignment = {
      horizontal: "left",
      vertical: "top",
      wrapText: true,
    };

    // Add borders to all merged cells
    for (let row = currentRow; row <= currentRow + 8; row++) {
      for (let col = 3; col <= 7; col++) {
        const cell = endCoverSheet.getCell(row, col);
        const isLastRow = row === currentRow + 8;
        cell.border = {
          bottom: {
            style: isLastRow ? "medium" : "",
            color: { argb: blueBorder },
          },
          left: { style: "medium", color: { argb: blueBorder } },
          right: { style: "medium", color: { argb: blueBorder } },
        };
      }
    }

    for (let i = 0; i < 9; i++) {
      endCoverSheet.getRow(currentRow + i).height = 20;
    }
    currentRow += 9;

    // Spacer
    endCoverSheet.getRow(currentRow).height = 8;
    currentRow++;

    // ===== "OUR TENDERS" SECTION =====
    const tendersHeaderCell = endCoverSheet.getCell(`C${currentRow}`);
    tendersHeaderCell.value = "Our tenders";
    endCoverSheet.mergeCells(`C${currentRow}:G${currentRow}`);
    tendersHeaderCell.font = {
      bold: true,
      size: 11,
      color: { argb: blackText },
      name: "Calibri",
    };
    tendersHeaderCell.alignment = { horizontal: "left", vertical: "middle" };

    // Add border to header row
    for (let col = 3; col <= 7; col++) {
      const cell = endCoverSheet.getCell(currentRow, col);
      cell.border = {
        top: { style: "medium", color: { argb: blueBorder } },
        left: { style: "medium", color: { argb: blueBorder } },
        right: { style: "medium", color: { argb: blueBorder } },
      };
    }
    endCoverSheet.getRow(currentRow).height = 22;
    currentRow++;

    // Tenders content - exact text from image
    const tendersText = `We work hard to produce quality information, and pride ourselves on the thoroughness of our tender information. Please consider this when analysing our submissions and comparing to those other businesses in the tender process.

Should you have any queries please ask.`;

    const tendersTextCell = endCoverSheet.getCell(`C${currentRow}`);
    tendersTextCell.value = tendersText;
    endCoverSheet.mergeCells(`C${currentRow}:G${currentRow + 3}`);
    tendersTextCell.font = {
      size: 9,
      color: { argb: blackText },
      name: "Calibri",
    };
    tendersTextCell.alignment = {
      horizontal: "left",
      vertical: "top",
      wrapText: true,
    };

    // Add borders to all merged cells
    for (let row = currentRow; row <= currentRow + 3; row++) {
      for (let col = 3; col <= 7; col++) {
        const cell = endCoverSheet.getCell(row, col);
        const isLastRow = row === currentRow + 3;
        cell.border = {
          bottom: {
            style: isLastRow ? "medium" : "thin",
            color: { argb: blueBorder },
          },
          left: { style: "medium", color: { argb: blueBorder } },
          right: { style: "medium", color: { argb: blueBorder } },
        };
      }
    }

    for (let i = 0; i < 4; i++) {
      endCoverSheet.getRow(currentRow + i).height = 22;
    }
    currentRow += 4;

    // ===== SPACER TO PUSH FOOTER TO BOTTOM =====
    for (let i = 0; i < 5; i++) {
      endCoverSheet.getRow(currentRow).height = 25;
      currentRow++;
    }

    // ===== FOOTER LOGOS WITH EQUAL SPACING =====
    const logoRow = currentRow;

    // Content area spans columns C-G (columns 3-7)
    // For equal spacing: [left margin] [logo1] [center space] [logo2] [right margin]
    // Positioning logos symmetrically within the content area

    // Logo 1: Supply Chain School (left side with equal spacing)
    try {
      console.log("🖼️ Loading footer logo 1 (Supply Chain School)...");
      const footerLogo1Buffer = await getImageBuffer(endCoverImg1);
      const footerLogo1Id = workbook.addImage({
        buffer: footerLogo1Buffer,
        extension: "png",
      });
      // Position with proper left margin for printing
      endCoverSheet.addImage(footerLogo1Id, {
        tl: { col: 2.3, row: logoRow } as any, // Adjusted for better left margin in print
        ext: { width: 90, height: 90 },
      });
      console.log("✅ Footer logo 1 placed with equal left spacing");
    } catch (error) {
      console.warn("⚠️ Could not load footer logo 1:", error);
    }

    // Logo 2: M&J Circular (right side with equal spacing)
    try {
      console.log("🖼️ Loading footer logo 2 (M&J circular)...");
      const footerLogo2Buffer = await getImageBuffer(endCoverImg2);
      const footerLogo2Id = workbook.addImage({
        buffer: footerLogo2Buffer,
        extension: "png",
      });
      // Position with equal right margin (mirroring left side)
      endCoverSheet.addImage(footerLogo2Id, {
        tl: { col: 6.1, row: logoRow } as any, // Adjusted to balance with left logo
        ext: { width: 100, height: 100 },
      });
      console.log("✅ Footer logo 2 placed with equal right spacing");
    } catch (error) {
      console.warn("⚠️ Could not load footer logo 2:", error);
    }

    // Reserve space for logos
    for (let i = 0; i < 5; i++) {
      endCoverSheet.getRow(currentRow + i).height = 25;
    }
    currentRow += 5;

    // ===== FOOTER COMPANY INFORMATION =====
    const footerStartRow = currentRow;

    // Top padding row with blue background
    for (let col = 2; col <= 8; col++) {
      const cell = endCoverSheet.getCell(currentRow, col);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: footerBlueBg }, // Footer blue background
      };
    }
    endCoverSheet.getRow(currentRow).height = 8;
    currentRow++;

    // Footer text row - full width (no left/right padding)
    for (let col = 2; col <= 8; col++) {
      const cell = endCoverSheet.getCell(currentRow, col);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: footerBlueBg }, // Footer blue background
      };
    }

    const footerCell = endCoverSheet.getCell(`B${currentRow}`);
    footerCell.value =
      "M & J Evans Construction Limited. Registered in England & Wales. Company Registration Number 04766812. Registered Office – 52-54 Wharf Approach, Anchor Brook Industrial Park, Aldridge, Walsall. WS9 8BX. Web: www.mjevansconstruction.co.uk";
    endCoverSheet.mergeCells(`B${currentRow}:H${currentRow}`);
    footerCell.font = {
      size: 7,
      color: { argb: "FFFFFF" }, // White text
      name: "Calibri",
    };
    footerCell.alignment = {
      horizontal: "left",
      vertical: "middle",
      wrapText: true,
    };
    endCoverSheet.getRow(currentRow).height = 30;
    currentRow++;

    // Bottom padding row with blue background
    for (let col = 2; col <= 8; col++) {
      const cell = endCoverSheet.getCell(currentRow, col);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: footerBlueBg }, // Footer blue background
      };
    }
    endCoverSheet.getRow(currentRow).height = 8;

    const lastContentRow = currentRow; // Save footer row position

    // Add bottom padding (empty rows after content) - reduced
    currentRow++;
    endCoverSheet.getRow(currentRow).height = 10; // Bottom padding (reduced)

    // ===== ADD OUTER BORDER AROUND ENTIRE PAGE =====
    console.log("🖼️ Adding outer page border...");
    const firstBorderRow = borderStartRow; // Row where border starts (includes padding)

    // Top border (across padding columns B-H)
    for (let col = 2; col <= 8; col++) {
      // B to H
      const cell = endCoverSheet.getCell(firstBorderRow, col);
      if (!cell.border) cell.border = {};
      cell.border = {
        ...cell.border,
        top: { style: "medium", color: { argb: blueBorder } },
      };
    }

    // Bottom border (across padding columns B-H)
    for (let col = 2; col <= 8; col++) {
      // B to H
      const cell = endCoverSheet.getCell(lastContentRow, col);
      if (!cell.border) cell.border = {};
      cell.border = {
        ...cell.border,
        bottom: { style: "medium", color: { argb: blueBorder } },
      };
    }

    // Left border (entire height from border start to footer)
    for (let row = firstBorderRow; row <= lastContentRow; row++) {
      const cell = endCoverSheet.getCell(row, 2); // Column B (padding column)
      if (!cell.border) cell.border = {};
      cell.border = {
        ...cell.border,
        left: { style: "medium", color: { argb: blueBorder } },
      };
    }

    // Right border (entire height from border start to footer)
    for (let row = firstBorderRow; row <= lastContentRow; row++) {
      const cell = endCoverSheet.getCell(row, 8); // Column H (padding column)
      if (!cell.border) cell.border = {};
      cell.border = {
        ...cell.border,
        right: { style: "medium", color: { argb: blueBorder } },
      };
    }

    console.log("✅ End cover sheet created successfully");
  } catch (error) {
    console.error("❌ Failed to create end cover sheet:", error);
    console.error(
      "Error type:",
      error instanceof Error ? error.constructor.name : typeof error,
    );
    console.error(
      "Error message:",
      error instanceof Error ? error.message : String(error),
    );
    if (error instanceof Error && error.stack) {
      console.error("Stack trace:", error.stack);
    }
    throw error;
  }
};

/**
 * Simplified end cover without images for debugging
 */
export const createEndCoverSimple = (
  workbook: ExcelWorkbook,
  data: CoverSheetData,
): void => {
  console.log("🎨 Creating simplified end cover sheet (no images)...");

  try {
    const endCoverSheet = workbook.addWorksheet("End Cover");

    endCoverSheet.columns = [{ width: 10 }, { width: 60 }, { width: 10 }];

    let row = 2;

    // Header
    const headerCell = endCoverSheet.getCell(`B${row}`);
    headerCell.value = `M & J EVANS CONSTRUCTION LIMITED ${data.region || "REGION"} - ${
      data.jobType || "JOB TYPE"
    } Tender Submission`;
    headerCell.font = { name: "Calibri", bold: true, size: 14 };
    row += 2;

    // Client info
    const clientCell = endCoverSheet.getCell(`B${row}`);
    clientCell.value = `${data.clientName} - ${
      data.siteAddress || "Site Address"
    } - ${
      data.dateCreated ? data.dateCreated.toLocaleDateString("en-GB") : "Date"
    }`;
    clientCell.font = { name: "Calibri", size: 11 };
    row += 3;

    // Our values
    const valuesCell = endCoverSheet.getCell(`B${row}`);
    valuesCell.value = "Our values.";
    valuesCell.font = { name: "Calibri", bold: true, size: 12 };
    row++;

    const valuesTextCell = endCoverSheet.getCell(`B${row}`);
    valuesTextCell.value =
      "Quality. Collaboration. Accountability. Integrity. Excellence";
    valuesTextCell.font = { name: "Calibri", size: 10 };
    row += 3;

    // Sustainability
    const sustainCell = endCoverSheet.getCell(`B${row}`);
    sustainCell.value = "Sustainability – a carbon neutral business.";
    sustainCell.font = { name: "Calibri", bold: true, size: 12 };
    row++;

    const sustainTextCell = endCoverSheet.getCell(`B${row}`);
    sustainTextCell.value = `At M&J we are acutely aware of the impact of our activities on the environment. The nature of our work means we are using machinery that burns fossil fuels. We are working with major manufacturers to select low emission equipment and trial the latest technology, such as Hybrid excavators. We have moved much of our company car fleet to Hybrid and electric; and our head office is now solar powered. We then purchase carbon credits to offset the emissions we cannot currently avoid.

M&J have signed up the Science Based Targets Initiative and are gold members of the Supply Chain Sustainability school.

We can work with you to reduce the carbon impact of build, sourcing materials such as low carbon concrete. Unless requested, these are not included in this tender, but we can provide costs as required.`;
    sustainTextCell.font = { name: "Calibri", size: 10 };
    sustainTextCell.alignment = { wrapText: true };
    endCoverSheet.mergeCells(`B${row}:C${row + 3}`);
    row += 5;

    // Our tenders
    const tendersCell = endCoverSheet.getCell(`B${row}`);
    tendersCell.value = "Our tenders";
    tendersCell.font = { name: "Calibri", bold: true, size: 12 };
    row++;

    const tendersTextCell = endCoverSheet.getCell(`B${row}`);
    tendersTextCell.value = `We work hard to produce quality information, and pride ourselves on the thoroughness of our tender information. Please consider this when analysing our submissions and comparing to those other businesses in the tender process.

Should you have any queries please ask.`;
    tendersTextCell.font = { name: "Calibri", size: 10 };
    tendersTextCell.alignment = { wrapText: true };
    endCoverSheet.mergeCells(`B${row}:C${row + 2}`);

    console.log("✅ Simplified end cover sheet created successfully");
  } catch (error) {
    console.error("❌ Failed to create simplified end cover sheet:", error);
    throw error;
  }
};
